import { useEffect, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';

import Button from 'domains/core/components/Button';

const DESCRIPTION_PLACEHOLDER = 'Describe what this role allows users to do…';

/** Matches RoleBuilderPage / API: role name must be 3–255 characters when trimmed. */
const MIN_ROLE_NAME_LEN = 3;

export type RoleNameDescriptionDialogMode = 'create' | 'duplicate';

export type RoleNameDescriptionDialogProps = {
    mode: RoleNameDescriptionDialogMode;
    open: boolean;
    onClose: () => void;
    /** Fires when user clicks Create or Duplicate with valid name (trimmed length ≥ {@link MIN_ROLE_NAME_LEN}). */
    onPrimary: (values: { name: string; description: string }) => void;
    initialName?: string;
    initialDescription?: string;
    /** Disables primary button and shows loading (e.g. duplicate API in flight). */
    isPrimaryLoading?: boolean;
};

const titles: Record<RoleNameDescriptionDialogMode, string> = {
    create: 'create role',
    duplicate: 'duplicate',
};

const primaryLabels: Record<RoleNameDescriptionDialogMode, string> = {
    create: 'Create',
    duplicate: 'Duplicate',
};

/** Same icon treatment as FolderNamingDialog (default size, paddingTop 2px). */
const dialogTitleIconSx = { paddingTop: '2px' } as const;

const roleNameDescriptionDialogContentSx = { overflow: 'visible' } as const;

const roleNameDescriptionDialogFormStackSx = { pt: 0.5 } as const;

const roleNameDescriptionDialogActionsSx = { px: 3, pb: 2 } as const;

const roleNameFieldHelperTextSx = { mt: 0.25, mx: 0, lineHeight: 1.25 } as const;

const roleNameFieldHelperTextErrorSx = {
    ...roleNameFieldHelperTextSx,
    mb: 0.5,
} as const;

const titleIcons: Record<RoleNameDescriptionDialogMode, JSX.Element> = {
    create: <PostAddOutlinedIcon sx={dialogTitleIconSx} aria-hidden />,
    duplicate: <ContentCopyIcon sx={dialogTitleIconSx} aria-hidden />,
};

const RoleNameDescriptionDialog = ({
    mode,
    open,
    onClose,
    onPrimary,
    initialName = '',
    initialDescription = '',
    isPrimaryLoading = false,
}: RoleNameDescriptionDialogProps) => {
    const [name, setName] = useState(initialName);
    const [description, setDescription] = useState(initialDescription);

    useEffect(() => {
        if (!open) return;
        setName(initialName);
        setDescription(initialDescription);
    }, [open, initialName, initialDescription]);

    const trimmedName = name.trim();
    const nameValid = trimmedName.length >= MIN_ROLE_NAME_LEN;
    const showNameLengthError = name.length > 0 && !nameValid;
    const primaryDisabled = !nameValid || isPrimaryLoading;

    const handlePrimary = () => {
        if (primaryDisabled) return;
        onPrimary({
            name: trimmedName,
            description: description.trim(),
        });
    };

    return (
        <Dialog
            data-testid={`role-name-description-dialog-${mode}`}
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="role-name-description-dialog-title"
        >
            <DialogTitle id="role-name-description-dialog-title">
                <Stack alignItems="center" direction="row" spacing={1}>
                    {titleIcons[mode]}
                    <Typography variant="h6">{titles[mode]}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={roleNameDescriptionDialogContentSx}>
                <Stack spacing={showNameLengthError ? 2.5 : 2} sx={roleNameDescriptionDialogFormStackSx}>
                    <TextField
                        label="Role name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        autoFocus
                        inputProps={{
                            maxLength: 255,
                            'data-testid': 'role-dialog-name-input',
                            'aria-required': true,
                        }}
                        error={showNameLengthError}
                        helperText={
                            showNameLengthError
                                ? `Role name must be at least ${MIN_ROLE_NAME_LEN} characters.`
                                : undefined
                        }
                        FormHelperTextProps={{
                            sx: showNameLengthError ? roleNameFieldHelperTextErrorSx : roleNameFieldHelperTextSx,
                        }}
                    />
                    <TextField
                        label="Role description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={DESCRIPTION_PLACEHOLDER}
                        fullWidth
                        multiline
                        minRows={3}
                        inputProps={{ maxLength: 2000, 'data-testid': 'role-dialog-description-input' }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={roleNameDescriptionDialogActionsSx}>
                <Button variant="text" onClick={onClose} data-testid="role-dialog-cancel-button">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handlePrimary}
                    disabled={primaryDisabled}
                    data-testid="role-dialog-primary-button"
                >
                    {primaryLabels[mode]}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RoleNameDescriptionDialog;
